'use client'

import { useState } from 'react'
import { gql } from 'graphql-request'
import { Trash2, AlertTriangle } from 'lucide-react'

import { useMutation } from '@hooks/graphql'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
  DialogClose
} from '@/components/ui/dialog'
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert'

const DELETE_BLOCK_MUTATION = gql`
  mutation deleteBlock($input: DeleteBlockInput!) {
    deleteBlock(input: $input) {
      success
      errors {
        message
      }
    }
  }
`

const DeleteBlockDialog = ({ trigger, blockId, refetch, onDeleted }) => {
  const [isOpen, setIsOpen] = useState(false)

  const { mutate: deleteBlock, isLoading: isDeleting } = useMutation({
    gqlMutation: DELETE_BLOCK_MUTATION,
    variables: { input: { blockId } },
    onSuccess: () => {
      setIsOpen(false)
      refetch()
      if (onDeleted) {
        onDeleted(blockId) // Notify parent if needed
      }
    }
  })

  const handleDelete = () => {
    deleteBlock()
  }

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        {trigger}
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete block?</DialogTitle>
        </DialogHeader>
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Warning</AlertTitle>
          <AlertDescription>
            Are you sure you'd like to delete this block? This can't be undone.
          </AlertDescription>
        </Alert>
        <DialogFooter className="sm:justify-between pt-3">
          <DialogClose asChild>
            <Button type="button" variant="outline" disabled={isDeleting}>
              Cancel
            </Button>
          </DialogClose>
          <Button
            type="button"
            variant="destructive"
            disabled={isDeleting}
            loading={isDeleting}
            onClick={handleDelete}
            className='w-fit'
          >
            <Trash2 className='mr-2 h-4 w-4' />
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default DeleteBlockDialog
